import React, { useState, useEffect, useRef } from "react";
import { Plus, Paperclip, Download, Trash2, Eye } from "lucide-react";
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox } from "../components/UI";
import { FilterBar, buildQS, initFilters } from "../components/FilterBar";
import Badge from "../components/Badge";
import { fmtRp, fmtDate, statusConfig } from "../utils/format";
import { paymentAPI, masterAPI } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

const PaymentPage = () => {
  const { user } = useAuth();
  const [data, setData]             = useState([]);
  const [filters, setFilters]       = useState(initFilters());
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [catatan, setCatatan]       = useState("");
  const [form, setForm] = useState({ keterangan:"", jumlah:"", vendor_id:"", akun_id:"", coa_detail_id:"", unit_id:"", anggaran_id:"" });
  const [refVendor, setRefVendor]   = useState([]);
  const [refAkun, setRefAkun]      = useState([]);
  const [refUnit, setRefUnit]      = useState([]);
  const [refAnggaran, setRefAnggaran] = useState([]);
  const [refCoaDetail, setRefCoaDetail] = useState([]);
  const [detailModal, setDetailModal]   = useState(null);
  const [detailData, setDetailData]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [uploading, setUploading]       = useState(false);
  const fileInputRef                    = useRef(null);

  const load = async (f = filters, s = search, sf = statusFilter) => {
    setLoading(true); setErr("");
    try {
      const res = await paymentAPI.getAll(buildQS(f, s, sf ? { status: sf } : {}));
      setData(res.data || []);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  const loadRefs = async () => {
    try {
      const [v, a, u, ag, cd] = await Promise.all([
        masterAPI.getVendor(),
        masterAPI.getCoaAkun(),
        masterAPI.getUnit(),
        masterAPI.getCoaAkun(),
        masterAPI.getCoaDetailAkun(),
      ]);
      setRefVendor((v.data||[]).filter(x=>x.status==="AKTIF").map(x=>({ value:x.id, label:x.nama })));
      setRefAkun((a.data||[]).map(x=>({
        value: x.id,
        label: `${x.kode} - ${x.nama}`,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
      })));
      setRefUnit((u.data||[]).map(x=>({ value:x.id, label:x.nama })));
      setRefAnggaran((ag.data||[]).map(x=>({ value:x.id, label:`${x.kode} - ${x.nama}` })));
      setRefCoaDetail((cd.data||[]).map(x=>({ value:x.id, label:`${x.kode} - ${x.nama}`, akun_id:x.akun_id })));
    } catch {}
  };

  useEffect(() => { load(filters, search, statusFilter); }, [filters, search, statusFilter]);
  useEffect(() => { loadRefs(); }, []);

  const handleCreate = async () => {
    if (!form.akun_id || !form.jumlah || !form.keterangan) return;
    setSaving(true);
    try {
      await paymentAPI.create({ ...form, jumlah: Number(form.jumlah), tahun_anggaran: Number(filters.tahun) });
      setShowModal(false);
      setForm({ keterangan:"", jumlah:"", vendor_id:"", akun_id:"", coa_detail_id:"", unit_id:"", anggaran_id:"" });
      load();
    } catch(e) { alert("Gagal membuat: " + e.message); }
    finally { setSaving(false); }
  };

  const openDetail = async (row) => {
    setDetailModal(row);
    setDetailLoading(true);
    try {
      const res = await paymentAPI.getById(row.id);
      setDetailData(res.data);
    } catch(e) { alert("Gagal memuat detail: " + e.message); }
    finally { setDetailLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detailModal) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await paymentAPI.uploadDokumen(detailModal.id, fd);
      const res = await paymentAPI.getById(detailModal.id);
      setDetailData(res.data);
    } catch(ex) { alert("Gagal upload: " + ex.message); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteDokumen = async (docId) => {
    if (!window.confirm("Hapus dokumen ini?")) return;
    try {
      await paymentAPI.deleteDokumen(detailModal.id, docId);
      const res = await paymentAPI.getById(detailModal.id);
      setDetailData(res.data);
    } catch(ex) { alert("Gagal hapus: " + ex.message); }
  };

  const fmtBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

  const handleAction = async (aksi) => {
    setSaving(true);
    try {
      await paymentAPI.action(actionModal.id, { aksi, catatan });
      setActionModal(null); setCatatan("");
      load();
    } catch(e) { alert("Gagal: " + e.message); }
    finally { setSaving(false); }
  };

  const canAct = (pr) => {
    if (pr.status==="PENDING_VERIFIKASI" && ["ADMIN","VERIFIKATOR"].includes(user.role)) return true;
    if (pr.status==="PENDING_APPROVAL"   && ["ADMIN","PEJABAT"].includes(user.role)) return true;
    if (pr.status==="DISETUJUI"          && ["ADMIN","BENDAHARA"].includes(user.role)) return true;
    return false;
  };

  const STATUS_OPTS = ["","PENDING_VERIFIKASI","PENDING_APPROVAL","DISETUJUI","DITOLAK","SELESAI"];
  const selectStyle = { padding:"8px 12px", border:"1.5px solid #E2E8F0", borderRadius:8, fontSize:13, outline:"none", background:"white", fontFamily:"inherit" };
  const selectedAkun = refAkun.find(x => x.value === form.akun_id);
  const filteredCoaDetail = form.akun_id ? refCoaDetail.filter(d => String(d.akun_id) === String(form.akun_id)) : [];
  const selectedCoaDetail = refCoaDetail.find(d => d.value === form.coa_detail_id);

  return (
    <div>
      <PageHeader title="Payment Request & Approval" subtitle="Pengajuan dan persetujuan permintaan pembayaran"
        action={<Btn icon={<Plus size={14}/>} onClick={()=>setShowModal(true)}>Buat Pengajuan</Btn>}
      />
      <FilterBar filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
        searchPlaceholder="Cari nomor SPP, keterangan, vendor…">
        <div>
          <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:5 }}>Status</label>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{ ...selectStyle, minWidth:160 }}>
            <option value="">Semua Status</option>
            {STATUS_OPTS.filter(Boolean).map(s=><option key={s} value={s}>{statusConfig[s]?.label||s}</option>)}
          </select>
        </div>
      </FilterBar>
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        {STATUS_OPTS.map(s => {
          const count = s ? data.filter(d=>d.status===s).length : data.length;
          return (
            <button key={s} onClick={()=>setStatusFilter(s)}
              style={{ padding:"5px 14px", borderRadius:20, border:"1.5px solid", cursor:"pointer", fontSize:12,
                fontWeight:600, fontFamily:"inherit",
                background:statusFilter===s?"#2563EB":"white", color:statusFilter===s?"white":"#6B7280",
                borderColor:statusFilter===s?"#2563EB":"#E5E7EB" }}>
              {s?(statusConfig[s]?.label||s):"Semua"} <span style={{ opacity:0.7 }}>({count})</span>
            </button>
          );
        })}
      </div>
      <Card>
        {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={()=>load()}/></div>}
        {loading ? <Spinner/> : (
          <Table pageSize={10} cols={[
            { key:"no_spp",     label:"No. SPP", nowrap:true },
            { key:"tanggal",    label:"Tanggal", render:v=>fmtDate(v), nowrap:true },
            { key:"program_kode", label:"Program", render:(v,r)=><ProgramBadge v={v}/> },
            { key:"kegiatan_kode", label:"Kegiatan", render:(v,r)=><KegiatanBadge v={v}/> },
            { key:"kro_kode",   label:"KRO", render:(v,r)=><KroBadge v={v}/> },
            { key:"kode_akun",  label:"Akun", render:(v,r)=>(
              <div>
                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#374151"}}>{v}</span>
                <br/><span style={{fontSize:11,color:"#6B7280"}}>{r.nama_akun}</span>
              </div>
            )},
            { key:"keterangan", label:"Keterangan" },
            { key:"vendor",     label:"Vendor" },
            { key:"jumlah",     label:"Jumlah", align:"right", render:v=><span style={{fontWeight:700,fontFamily:"monospace",fontSize:12}}>{fmtRp(v)}</span>, nowrap:true },
            { key:"status",     label:"Status", render:v=><Badge status={v}/> },
            { key:"id", label:"Aksi", render:(_,row)=>(
              <div style={{display:"flex",gap:6}}>
                <Btn size="sm" variant="secondary" icon={<Eye size={12}/>} onClick={()=>openDetail(row)}>Detail</Btn>
                {canAct(row) && <Btn size="sm" onClick={()=>setActionModal(row)}>Proses</Btn>}
              </div>
            )},
          ]} data={data}/>
        )}
      </Card>

      {showModal && (
        <Modal title="Buat Payment Request" onClose={()=>setShowModal(false)}>
          <Field label="Keterangan" required><Input value={form.keterangan} onChange={e=>setForm({...form,keterangan:e.target.value})} placeholder="Deskripsi kebutuhan"/></Field>
          <Field label="Jumlah (Rp)" required><Input value={form.jumlah} onChange={e=>setForm({...form,jumlah:e.target.value})} type="number" placeholder="0"/></Field>
          <Field label="Akun MA" required>
            <Select value={form.akun_id} onChange={e=>setForm({...form,akun_id:e.target.value, coa_detail_id:""})} options={refAkun} placeholder="Pilih akun…"/>
            {selectedAkun && (
              <div style={{ marginTop:8, padding:10, background:"#F8FAFC", borderRadius:8, fontSize:12, display:"flex", gap:6, flexWrap:"wrap" }}>
                <ProgramBadge v={selectedAkun.program_kode ? `${selectedAkun.program_kode} - ${selectedAkun.program_nama}` : null}/>
                <KegiatanBadge v={selectedAkun.kegiatan_kode ? `${selectedAkun.kegiatan_kode} - ${selectedAkun.kegiatan_nama}` : null}/>
                <KroBadge v={selectedAkun.kro_kode ? `${selectedAkun.kro_kode} - ${selectedAkun.kro_nama}` : null}/>
              </div>
            )}
          </Field>
          {form.akun_id && (
            <Field label="Detail COA" required>
              <Select value={form.coa_detail_id} onChange={e=>setForm({...form,coa_detail_id:e.target.value})} options={filteredCoaDetail} placeholder="Pilih Detail COA…"/>
              {selectedCoaDetail && (
                <div style={{ marginTop:8, padding:8, background:"#EFF6FF", borderRadius:8, fontSize:12 }}>
                  <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{selectedCoaDetail.label}</span>
                </div>
              )}
            </Field>
          )}
          <Field label="Unit Kerja">
            <Select value={form.unit_id} onChange={e=>setForm({...form,unit_id:e.target.value})} options={refUnit} placeholder="Pilih unit…"/>
          </Field>
          <Field label="Vendor">
            <Select value={form.vendor_id} onChange={e=>setForm({...form,vendor_id:e.target.value})} options={refVendor} placeholder="Pilih vendor…"/>
          </Field>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
            <Btn variant="secondary" onClick={()=>setShowModal(false)}>Batal</Btn>
            <Btn onClick={handleCreate} disabled={saving}>{saving?"Mengajukan…":"Ajukan"}</Btn>
          </div>
        </Modal>
      )}

      {detailModal && (
        <Modal title={`Detail: ${detailModal.no_spp}`} onClose={()=>{ setDetailModal(null); setDetailData(null); }}>
          {detailLoading ? <Spinner/> : detailData && (
            <>
              <div style={{ background:"#F8FAFC", borderRadius:10, padding:16, marginBottom:20 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:13 }}>
                  {[["Keterangan",detailData.keterangan],["Vendor",detailData.vendor||"-"],["Jumlah",fmtRp(detailData.jumlah)],["Tanggal",fmtDate(detailData.tanggal)]].map(([k,v])=>(
                    <div key={k}>
                      <p style={{margin:"0 0 2px",fontSize:11,color:"#9CA3AF",fontWeight:600}}>{k}</p>
                      <p style={{margin:0,fontWeight:600,color:"#111827"}}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontWeight:700, fontSize:13, color:"#374151" }}>
                  <Paperclip size={14} style={{verticalAlign:"middle",marginRight:4}}/>
                  Dokumen Pendukung ({(detailData.dokumen||[]).length})
                </span>
                <div>
                  <input ref={fileInputRef} type="file" style={{display:"none"}}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={handleUpload}/>
                  <Btn size="sm" icon={<Plus size={12}/>} onClick={()=>fileInputRef.current?.click()} disabled={uploading}>
                    {uploading ? "Mengunggah…" : "Upload"}
                  </Btn>
                </div>
              </div>

              {(detailData.dokumen||[]).length === 0 ? (
                <div style={{ textAlign:"center", padding:"20px 0", color:"#9CA3AF", fontSize:13 }}>Belum ada dokumen diunggah</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {(detailData.dokumen||[]).map(doc=>(
                    <div key={doc.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"10px 14px", background:"#F9FAFB", borderRadius:8, border:"1px solid #E5E7EB" }}>
                      <div>
                        <p style={{margin:0,fontSize:13,fontWeight:600,color:"#111827"}}>{doc.nama_asli||doc.nama_file}</p>
                        <p style={{margin:0,fontSize:11,color:"#9CA3AF"}}>{fmtBytes(doc.ukuran_bytes)} · {fmtDate(doc.uploaded_at)}</p>
                      </div>
                      <div style={{display:"flex",gap:6}}>
                        <a href={paymentAPI.downloadUrl(detailModal.id, doc.id)} target="_blank" rel="noreferrer"
                          style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"5px 10px",
                            background:"#EFF6FF", color:"#2563EB", borderRadius:6, fontSize:12, fontWeight:600,
                            textDecoration:"none", border:"1px solid #BFDBFE" }}>
                          <Download size={12}/> Unduh
                        </a>
                        <button onClick={()=>handleDeleteDokumen(doc.id)}
                          style={{ display:"inline-flex", alignItems:"center", padding:"5px 10px",
                            background:"#FEF2F2", color:"#DC2626", borderRadius:6, fontSize:12,
                            fontWeight:600, border:"1px solid #FECACA", cursor:"pointer" }}>
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p style={{fontSize:11,color:"#9CA3AF",marginTop:12}}>Format: PDF, JPG, PNG, DOC, XLS · Maks: 10MB</p>
            </>
          )}
        </Modal>
      )}

      {actionModal && (
        <Modal title={`Proses: ${actionModal.no_spp}`} onClose={()=>setActionModal(null)}>
          <div style={{ background:"#F8FAFC", borderRadius:10, padding:16, marginBottom:20 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {[["Keterangan",actionModal.keterangan],["Vendor",actionModal.vendor||"-"],["Jumlah",fmtRp(actionModal.jumlah)],["Status",""]].map(([k,v])=>(
                <div key={k}>
                  <p style={{ margin:"0 0 2px", fontSize:11, color:"#9CA3AF", fontWeight:600 }}>{k}</p>
                  {k==="Status"?<Badge status={actionModal.status}/>:<p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111827" }}>{v}</p>}
                </div>
              ))}
            </div>
          </div>
          <Field label="Catatan">
            <textarea value={catatan} onChange={e=>setCatatan(e.target.value)} placeholder="Catatan (opsional)" rows={3}
              style={{ width:"100%", padding:"9px 12px", border:"1.5px solid #E5E7EB", borderRadius:8,
                fontSize:13, outline:"none", resize:"vertical", fontFamily:"inherit", boxSizing:"border-box" }}/>
          </Field>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn variant="secondary" onClick={()=>setActionModal(null)}>Batal</Btn>
            <Btn variant="danger"   onClick={()=>handleAction("TOLAK")}     disabled={saving}>Tolak</Btn>
            {actionModal.status==="PENDING_VERIFIKASI" && <Btn variant="success" onClick={()=>handleAction("VERIFIKASI")} disabled={saving}>Verifikasi</Btn>}
            {actionModal.status==="PENDING_APPROVAL"   && <Btn variant="success" onClick={()=>handleAction("SETUJUI")}    disabled={saving}>Setujui</Btn>}
            {actionModal.status==="DISETUJUI"          && <Btn variant="primary" onClick={()=>handleAction("EKSEKUSI")}   disabled={saving}>Eksekusi Bayar</Btn>}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PaymentPage;